import { format, parseISO } from "date-fns";
import { CalendarCheck, CalendarPlus, Check, FileText, Flag, Link2, Undo2 } from "lucide-react";
import { fmtDuration } from "@/lib/time";
import { href } from "@/lib/tasks";

function SubLine({ s, j, canEdit, onToggle, testId }) {
  return (
    <li className="flex items-center gap-2.5 py-1.5" data-testid={testId}>
      <input type="checkbox" checked={s.done} disabled={!canEdit} onChange={() => onToggle(j)} className="w-4 h-4 accent-[#103080] cursor-pointer" data-testid={`${testId}-check`} />
      <span className={`flex-1 text-sm text-[#0F172A]/80 ${s.done ? "line-through opacity-50" : ""}`}>{s.title}</span>
      {s.sop_link && <a href={href(s.sop_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0090D0] hover:underline" data-testid={`${testId}-sop`}><FileText size={11} />SOP</a>}
      {s.template_link && <a href={href(s.template_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0090D0] hover:underline" data-testid={`${testId}-link`}><Link2 size={11} />Template</a>}
      {s.duration_minutes > 0 && <span className="text-[11px] text-[#0F172A]/45 w-12 text-right">{fmtDuration(s.duration_minutes)}</span>}
    </li>
  );
}

function Planner({ t, onSchedule, onPatch }) {
  if (t.in_planner) {
    return (
      <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#0090D0]" data-testid={`card-task-booked-${t.id}`}>
        <CalendarCheck size={12} />{t.blocks.length ? format(parseISO(t.blocks[0].start), "EEE d MMM, HH:mm") : "Waiting for a slot"}
        <button onClick={() => onPatch(t, { in_planner: false }, "Removed from schedule")} data-testid={`card-task-unschedule-${t.id}`}
          className="text-[#103080]/50 hover:text-red-600 inline-flex items-center gap-0.5 font-normal transition-colors duration-200"><Undo2 size={11} />Remove</button>
      </span>
    );
  }
  return (
    <button onClick={() => onSchedule(t)} data-testid={`card-task-schedule-${t.id}`}
      className="inline-flex items-center gap-1 rounded-full border border-[#103080]/20 px-3 py-1 text-xs font-semibold text-[#103080] hover:bg-[#103080] hover:text-white transition-colors duration-200">
      <CalendarPlus size={12} /> Add to planner
    </button>
  );
}

export default function CardTask({ t, who, canEdit, onSchedule, onPatch, onSubtasks }) {
  const done = t.status === "done";
  const subs = t.subtasks || [];
  const ticked = subs.filter((s) => s.done).length;
  const toggle = (j) => onSubtasks(t, subs.map((s, k) => (k === j ? { ...s, done: !s.done } : s)));
  return (
    <li className="rounded-xl border border-[#103080]/10 bg-white p-4" data-testid={`card-task-${t.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <button disabled={!canEdit} onClick={() => onPatch(t, { status: done ? "todo" : "done" })} aria-label="Toggle task done" data-testid={`card-task-done-${t.id}`}
          className={`mt-0.5 w-5 h-5 shrink-0 rounded-full border flex items-center justify-center transition-colors duration-200 ${done ? "bg-[#103080] border-[#103080] text-white" : "border-[#103080]/30 hover:border-[#0090D0]"}`}>
          {done && <Check size={12} />}
        </button>
        <div className="flex-1 min-w-[200px]">
          <p className={`font-semibold text-[#103080] ${done ? "line-through opacity-50" : ""}`}>{t.title}</p>
          <p className="text-xs text-[#0F172A]/55 mt-0.5">{t.role && <span className="font-semibold text-[#0090D0]">{t.role} · </span>}{who || "Unassigned"} · <b data-testid={`card-task-total-${t.id}`}>{fmtDuration(t.duration_minutes)}</b>
            {subs.length > 0 && <span data-testid={`card-task-subprogress-${t.id}`}> · {ticked}/{subs.length} subtasks</span>}</p>
          {t.sets_status && <p className="text-[11px] text-[#0F172A]/45 mt-0.5 inline-flex items-center gap-1"><Flag size={10} />When done, status becomes {t.sets_status}</p>}
        </div>
        {!done && canEdit && <Planner t={t} onSchedule={onSchedule} onPatch={onPatch} />}
      </div>
      {subs.length > 0 && (
        <>
          <div className="mt-3 h-1 rounded-full bg-[#F5F2EB] overflow-hidden"><div className="h-full bg-[#0090D0] transition-[width] duration-300" style={{ width: `${(ticked / subs.length) * 100}%` }} /></div>
          <ul className="mt-2 pl-8 divide-y divide-[#103080]/5">
            {subs.map((s, j) => <SubLine key={j} s={s} j={j} canEdit={canEdit} onToggle={toggle} testId={`card-sub-${t.id}-${j}`} />)}
          </ul>
        </>
      )}
    </li>
  );
}
